import * as React from 'react';
import styled from 'styled-components';
import gettext from '@sdk/lang/gettext';
import Text14 from '@sdk/components/ui/typography/Text14';
import TextField from '@sdk/components/forms/TextField';
import Column from '@sdk/components/ui/spacing/Column';
import useAuth from '@sdk/authentication/useAuth';
import validateNumber, {
  ValidateOptions,
  ValidNumberOption,
} from '@sdk/utils/validation/validateNumber';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import type { UpdateLaborCostSettings } from '@sdk/labor/types';

import CollapsibleSettingsSection from '../CollapsibleSettingsSection';
import RowSpaceBetween from '../RowSpaceBetween';
import SettingsSwitcherRow from '../SettingsSwitcherRow';
import { FIELD_WIDTH_205 } from '../../constants';
import { useSettingsErrorsContext } from '../../SettingsErrorContext';
import { TabType } from '../../types';

const StyledTextField = styled(TextField)`
  max-width: ${FIELD_WIDTH_205}px;
`;

type Props = {
  laborPercentEnabled: boolean;
  laborPercent: number | string;
  onLaborCostSettingsChange: UpdateLaborCostSettings;
};

const LaborPercent: React.FC<React.PropsWithChildren<Props>> = ({
  onLaborCostSettingsChange,
  laborPercentEnabled,
  laborPercent,
}) => {
  const { toggleError } = useSettingsErrorsContext();
  const { currentUser } = useAuth();

  const validateLaborPercent = (value: number | string): boolean => {
    return validateNumber(value, {
      [ValidateOptions.validNumber]: ValidNumberOption.nonNegative,
      [ValidateOptions.validNumberLowerOrEqualThan]: 100,
    });
  };
  return (
    <CollapsibleSettingsSection
      pBottom={20}
      withSeparator
      isActive={laborPercentEnabled}
      headerItem={
        <SettingsSwitcherRow
          subtitle={gettext('Compare your labor spending against your sales.')}
          value={laborPercentEnabled}
          onChange={(value: boolean): void =>
            onLaborCostSettingsChange({ laborPercentEnabled: value })
          }
          disabled={!currentUser.isAdmin}
        />
      }
      contentItem={
        <RowSpaceBetween>
          <Column>
            <Text14 semiBold themeColor={ThemeColorType.gray}>
              {gettext('Labor % goal')}
            </Text14>
            <Text14 themeColor={ThemeColorType.gray}>
              {gettext("Set the upper limit, which you won't want to exceed.")}
            </Text14>
          </Column>

          <StyledTextField
            skipErrorMessage
            postfix={'%'}
            value={laborPercent}
            onChange={(event): void => {
              onLaborCostSettingsChange({
                laborPercent: event.currentTarget.value,
              });
              toggleError(
                TabType.laborCost,
                'laborPercent',
                !validateLaborPercent(event.currentTarget.value)
              );
            }}
            invalid={!validateLaborPercent(laborPercent)}
            disabled={!currentUser.isAdmin}
          />
        </RowSpaceBetween>
      }
    />
  );
};

export default LaborPercent;
